import {useMemo} from "react";
import {useQuery} from "react-query";
import {useSelector} from "react-redux";
import type {User} from "crqlar-react-common/dist/models/identity/user.model";
import {PrintoutType} from "crqlar-react-common/dist/models/printout/printout.model";
import {SectorService} from "crqlar-react-common/dist/services/sector/sector.service";
import {useOrganization} from "crqlar-react-common/dist/state/auth.reducer";
import type {PrintoutVariableResolveContext} from "../variables/printoutVariableResolver";
import {getSamplePreviewContext} from "./samplePreviewContext";

interface PrintoutPreviewContextState {
    context: PrintoutVariableResolveContext;
    loading: boolean;
}

// Hotel and staff details are real; guest and booking records remain samples while designing a template.
export const usePrintoutPreviewContextState = (
    printoutType?: PrintoutType | null,
    language?: string,
    scopedSectorIds?: string[],
): PrintoutPreviewContextState => {
    const organization = useOrganization();
    const user = useSelector<any, User | undefined>(state => state.auth.user);
    const previewSectorId = scopedSectorIds?.[0];
    const sectorQuery = useQuery(
        ["printoutPreview", "sector", previewSectorId],
        () => SectorService.getSectorById(previewSectorId!),
        {
            enabled: (
                printoutType === PrintoutType.RESERVATION_CONFIRMATION
                || printoutType === PrintoutType.RESERVATION_CANCELLATION
            ) && Boolean(previewSectorId),
            staleTime: 5 * 60 * 1000,
        },
    );

    const context = useMemo<PrintoutVariableResolveContext>(() => ({
        ...getSamplePreviewContext(printoutType, language, sectorQuery.data?.sectorType),
        // Do not fill missing contact or legal fields with the sample hotel's values.
        organization: organization ? {...organization} : undefined,
        user: user ? {...user} : undefined,
    }), [
        language,
        organization,
        printoutType,
        sectorQuery.data?.sectorType,
        user,
    ]);

    return {
        context,
        loading: sectorQuery.isLoading,
    };
};

export const usePrintoutPreviewContext = (
    printoutType?: PrintoutType | null,
    language?: string,
    scopedSectorIds?: string[],
) => usePrintoutPreviewContextState(printoutType, language, scopedSectorIds).context;
